import { Button } from "@msuite/ui/button";
import { CopyButton } from "@msuite/ui/copy";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Popover } from "@msuite/ui/popover";
import { QRCode } from "@msuite/ui/qr-code";
import { useQuery } from "@msuite/ui/query";
import { Tooltip } from "@msuite/ui/tooltip";
import { responseHandler } from "@msuite/ui/utils";
import { getPartnerConnectLink } from "@msuite/utils/organizations";
import { guardString } from "@msuite/utils/strings";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";

/**
 * The organisation's partnership code, shown three ways: the code itself, the web link that
 * redeems it in any browser — also for a company not yet in the system — and a QR code.
 */
export function ConnectOrganizationForm() {
	/** Hooks */
	const { t } = useTranslation();
	const { organization } = useAuth();

	/** Query */
	const { data } = useQuery({
		retry: false,
		queryKey: ["inviation-code", "partnershipInvitationCode"],
		queryFn: async () => {
			const response = await client.organizations[":orgId"]["invitation-codes"][":type"].$get({
				param: {
					orgId: guardString(organization?.id),
					type: "partnershipInvitationCode",
				},
			});
			return responseHandler(response);
		},
	});

	/** Computed */
	// The QR carries the native deep link, so a phone's system camera opens the app on its connect
	// route; the copied link is the web one, which works without the app.
	const qrValue = data?.invitationCode ? getPartnerConnectLink(data.invitationCode) : null;

	/** Render */
	return (
		<div className="h-stack justify-between gap-8 items-start">
			<div className="v-stack">
				<p className="text-sm font-semibold">{t("organization.partnership_invitation_code")}</p>
				<p className="label">{t("organization.partnership_invitation_code_description")}</p>
				<p className="text-3xl font-bold tabular-nums">{guardString(data?.invitationCode)}</p>
				<p className="label">{t("organization.connect_link_description")}</p>
			</div>
			<div className="h-stack gap-2">
				<Tooltip label={t("organization.copy_connect_link")}>
					<CopyButton
						value={data?.connectLink}
						label={t("organization.copy_connect_link")}
					/>
				</Tooltip>
				<Tooltip label={t("organization.show_qr_code")}>
					<Popover
						trigger="click"
						render={() => <QRCode value={qrValue} />}
					>
						<Button
							icon={<Icon name="QrCode" />}
							aria-label={t("organization.show_qr_code")}
						/>
					</Popover>
				</Tooltip>
			</div>
		</div>
	);
}
